import { calculatePasswordStrength, type PasswordStrength } from "@/lib/password-strength"
import { useEffect, useState } from "react"

interface PasswordStrengthMeterProps {
  password: string
  email?: string
}

export function PasswordStrengthMeter({ password, email }: PasswordStrengthMeterProps) {
  const [strength, setStrength] = useState<PasswordStrength | null>(null)

  useEffect(() => {
    if (password) {
      setStrength(calculatePasswordStrength(password, email))
    } else {
      setStrength(null)
    }
  }, [password, email])

  if (!strength || !password) return null

  const getColorClasses = () => {
    switch (strength.color) {
      case 'red':
        return 'bg-red-500/20 border-red-500/40 text-red-300'
      case 'yellow':
        return 'bg-yellow-500/20 border-yellow-500/40 text-yellow-300'
      case 'green':
        return 'bg-green-500/20 border-green-500/40 text-green-300'
      default:
        return 'bg-white/10 border-white/20 text-white/70'
    }
  }

  const getProgressColor = () => {
    switch (strength.color) {
      case 'red':
        return 'bg-red-500'
      case 'yellow':
        return 'bg-yellow-500'
      case 'green':
        return 'bg-green-500'
      default:
        return 'bg-white/30'
    }
  }

  const progressWidth = `${(strength.score / 4) * 100}%`

  return (
    <div className="space-y-2">
      {/* Progress bar */}
      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getProgressColor()} transition-all duration-300 ease-out`}
          style={{ width: progressWidth }}
        />
      </div>

      {/* Label and feedback */}
      <div className={`rounded-full px-3 py-1.5 border text-xs ${getColorClasses()}`}>
        <div className="flex items-center justify-between">
          <span className="font-medium">{strength.label}</span>
          {strength.feedback.length > 0 && (
            <span className="text-[10px] opacity-80">
              {strength.feedback.join(', ')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

